//
// Copyright (c) 2011-2026 by Curalate, Inc.
//

import {
  TikTokMusicGenre,
  type TikTokMusicGenre as TikTokMusicGenreType,
} from '@apis/tiktok-trends/tiktokMusicGenre';
import {
  TikTokPopularCommercialTrack,
  TikTokPopularCommercialTrackClip,
} from '@apis/tiktok-trends/tiktokTrendsModel';
import { TikTokMusicSoundInfoConfig } from '@apis/universal-posts/config/universalScheduledPostConfigModel';
import { isDefined } from '@utils/filterPredicates';
import { getMsToSeconds, getSecondsToMs } from '@utils/timingUtils';

export const musicSoundInfoMsToSeconds = (
  info: TikTokMusicSoundInfoConfig | undefined,
): TikTokMusicSoundInfoConfig | undefined => {
  if (!isDefined(info)) {
    return undefined;
  }
  return {
    ...info,
    start: isDefined(info.start) ? getMsToSeconds(info.start) : undefined,
    end: isDefined(info.end) ? getMsToSeconds(info.end) : undefined,
  };
};

export const musicSoundInfoSecondsToMs = (
  info: TikTokMusicSoundInfoConfig | undefined,
): TikTokMusicSoundInfoConfig | undefined => {
  if (!isDefined(info)) {
    return undefined;
  }
  return {
    ...info,
    start: isDefined(info.start) ? getSecondsToMs(info.start) : undefined,
    end: isDefined(info.end) ? getSecondsToMs(info.end) : undefined,
  };
};

/**
 * Converts a TikTok music genre enum value into a human-readable label.
 * Replaces underscores with spaces and normalizes each word to title case
 * by uppercasing the first letter after a non-letter boundary and
 * lowercasing subsequent letters.
 */
export const getTikTokGenreLabel = (genre: TikTokMusicGenreType): string => {
  if (genre === TikTokMusicGenre.All) {
    return 'All genres';
  }

  return genre
    .replaceAll('_', ' ')
    .toLowerCase()
    .replace(/\b\w/g, (match) => match.toUpperCase());
};

export const resolveTrackInfo = (
  track: TikTokPopularCommercialTrack,
): TikTokPopularCommercialTrackClip => {
  // Select full duration clip if available, otherwise fallback to trending clip.
  const resolved = [track.fullDurationSongClip, track.trendingSongClip]
    .filter(isDefined)
    .filter(
      (clip) =>
        isDefined(clip.duration) &&
        isDefined(clip.songClipId) &&
        isDefined(clip.previewUrl),
    )
    .map((clip) => ({
      duration: clip.duration,
      songClipId: clip.songClipId,
      previewUrl: clip.previewUrl,
    }));

  return (
    resolved[0] ?? {
      duration: undefined,
      songClipId: undefined,
      previewUrl: undefined,
    }
  );
};
